import moment from "moment";
import { useEffect } from "react";
import { useQuery } from "react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KTIcon } from "../../../../_metronic/helpers";
import { PageHeader } from "../../../components/common/pageHeader/PageHeader";
import { Pagination } from "../../../components/common/pagination/Pagination";
import { MyTooltip } from "../../../components/common/tooltip/Tooltip";
import { EmptySection } from "../../../components/sections/empty/Empty";
import { LoaderSection } from "../../../components/sections/loader/Loader";
import { useDeleteModal } from "../../../context/DeleteModalContext";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  URL_VOTE_CATEGORY_CREATE,
  URL_VOTE_REQUESTS,
} from "../../../mock/page_urls";
import { MODERATOR_QUESTIONARE_CATEGORY } from "../../../mock/URLs";
import {
  getVoteQuestionCategory,
  updateVoteQuestion,
} from "../../../services/voteQuestionSvc";
import { separateFormatter } from "../../../utils/separateFormatter";

export const VoteCategoriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const searchQuery = searchParams.get("search") || "";

  const debouncedSearch = useDebounce(searchQuery, 500);

  const {
    data: categoryData,
    refetch: refetchCategory,
    isLoading: categoryLoading,
  } = useQuery(["getVoteQuestionCategory", page, debouncedSearch], () =>
    getVoteQuestionCategory({
      params: {
        page,
        page_size: 8,
        search: debouncedSearch,
      },
    })
  );
  const navigate = useNavigate();
  const pageDetails = {
    title: "So'rovnoma kategoriyalari",
    desc: `Jami so'rovnomalar soni: (${separateFormatter(
      categoryData?.data.length || 0,
      " ",
      "ta"
    )})`,
    buttons: [
      {
        onClick: () => navigate(URL_VOTE_REQUESTS),
        text: "Murojaatlar",
        className: "btn-light-primary",
        icon: "chat",
      },
      {
        onClick: () => navigate(URL_VOTE_CATEGORY_CREATE),
        text: "Qo'shish",
        icon: "plus-square",
      },
    ],
  };

  const totalItems = categoryData?.total_items || 0;
  const itemsPerPage = categoryData?.page_size || 8;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchParams({ page: "1", search: query });
  };

  useEffect(() => {
    if (debouncedSearch) {
      refetchCategory();
    }
  }, [debouncedSearch, refetchCategory]);

  return (
    <div>
      <PageHeader pageDetails={pageDetails} />
      <div
        className={
          "d-flex flex-wrap justify-content-between align-items-center rounded py-1 mb-5"
        }
      >
        <div className={"w-100 w-100 w-md-250px"}>
          <div className="d-flex align-items-center position-relative my-1">
            <KTIcon
              iconName={"magnifier"}
              className={"fs-1 position-absolute ms-3"}
            />

            <input
              type="text"
              data-kt-user-table-filter="search"
              className="form-control w-100 w-md-250px ps-12"
              placeholder="Qidiruv..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {categoryLoading ? (
        <LoaderSection height={"50vh"} />
      ) : !categoryData || categoryData.data.length === 0 ? (
        <EmptySection height={"50vh"} />
      ) : (
        <>
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-4 g-6 mb-10">
            {categoryData?.data.map((category) => (
              <VoteCategoryCard
                category={category}
                refetchCategory={refetchCategory}
              />
            ))}
          </div>
          <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} />
        </>
      )}
    </div>
  );
};

const VoteCategoryCard = ({ category, refetchCategory }) => {
  const { openDeleteModal } = useDeleteModal();

  const handleDeleteClick = (id: number) => {
    openDeleteModal(MODERATOR_QUESTIONARE_CATEGORY, id, refetchCategory);
  };

  const handleStatusChange = async (id: number, currentStatus: boolean) => {
    try {
      await updateVoteQuestion(id, { is_active: !currentStatus });
      await refetchCategory();
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };
  return (
    <div key={category.id}>
      <div className={"card"}>
        <div className="card-body p-3">
          <h5 className="card-title fw-bold text-dark mb-2 text-truncate">
            {category.title}
          </h5>
          <span className="text-muted mb-0 fs-7">
            {moment(category.created_at!).format("DD.MM.YYYY HH:mm:ss")}
          </span>
        </div>
        <div
          className={
            "card-footer p-3 d-flex justify-content-between align-items-center"
          }
        >
          <Link
            to={`/vote-category/update/${category.id}`}
            className={"btn btn-sm btn-light-primary"}
          >
            <KTIcon iconName={"pencil"} className={"fs-4"} />
            Tahrirlash
          </Link>

          <MyTooltip title={"Holatni aktivlashtirish"} position="top">
            <div className="form-check form-check-solid form-switch fv-row">
              <input
                className="form-check-input w-45px h-30px"
                type="checkbox"
                name={"is_active"}
                checked={category.is_active}
                onChange={() =>
                  handleStatusChange(category.id, category.is_active)
                }
              />
              <label className="form-check-label"></label>
            </div>
          </MyTooltip>

          <button
            onClick={() => handleDeleteClick(category.id)}
            className="btn btn-icon btn-danger btn-sm me-1"
          >
            <KTIcon iconName={"trash-square"} className={"pe-0 fs-3"} />
          </button>
        </div>
      </div>
    </div>
  );
};
